import { findMainServiceTrenchingEvaluateCode } from './mainServicesEvaluateCodesMapping';

export interface MSItem {
  id: string;
  description: string;
  unit: string;
  ref?: string;
  isNote?: boolean;
}

export interface MSGroup {
  id: string;
  label: string;
  items: MSItem[];
}

export const MAIN_SERVICE_TRENCHING_MAPPING: MSGroup[] = [
  {
    id: 'water',
    label: 'Water',
    items: [
      { id: 'water-main-trenching', description: 'Water main trenching', unit: 'm' },
    ],
  },
  {
    id: 'electric',
    label: 'Electric',
    items: [
      { id: 'electric-lv-trenching', description: 'Electric LV main trenching', unit: 'm' },
      { id: 'electric-hv-trenching', description: 'Electric HV main trenching to substation', unit: 'm' },
      { id: 'substation-base', description: 'Substation base & maintenance drive', unit: 'item' },
    ],
  },
  {
    id: 'gas',
    label: 'Gas',
    items: [
      { id: 'gas-main-trenching', description: 'Gas main trenching', unit: 'm' },
    ],
  },
  {
    id: 'bt-ducting',
    label: 'BT Ducting',
    items: [
      { id: 'bt-main-ducting', description: 'BT main ducting', unit: 'm' },
      { id: 'bt-eo-2way', description: 'EO 2 way ducting', unit: 'm' },
      { id: 'bt-eo-3way', description: 'EO 3 way ducting', unit: 'm' },
      { id: 'bt-eo-4way', description: 'EO 4 way ducting', unit: 'm' },
      { id: 'bt-jbf-104', description: 'BT Joint Box JBF 104', unit: 'Nr' },
      { id: 'bt-jbf-104c', description: 'BT Joint Box JBF 104C 750mm deep', unit: 'Nr' },
      { id: 'bt-jbf-106d', description: 'BT Joint Box JBF 106D 900 deep', unit: 'Nr' },
      { id: 'bt-jbf-106', description: 'BT Joint Box JBF 106 1200 deep', unit: 'Nr' },
    ],
  },
  {
    id: 'bt-ducting-stakkabox',
    label: 'BT Ducting (Stakkabox)',
    items: [
      { id: 'bt-jbf-104-s', description: 'BT Joint Box JBF 104 (utilising pre-formed Stakkabox)', unit: 'Nr' },
      { id: 'bt-jbf-104c-s', description: 'BT Joint Box JBF 104C 750mm deep (utilising pre-formed Stakkabox)', unit: 'Nr' },
      { id: 'bt-jbf-106d-s', description: 'BT Joint Box JBF 106D 900 deep (utilising pre-formed Stakkabox)', unit: 'Nr' },
      { id: 'bt-jbf-106-s', description: 'BT Joint Box JBF 106 1200 deep (utilising pre-formed Stakkabox)', unit: 'Nr' },
    ],
  },
  {
    id: 'virgin-media',
    label: 'Virgin Media',
    items: [
      { id: 'vm-main-ducting', description: 'Virgin Media main ducting', unit: 'm' },
      { id: 'vm-eo-2way', description: 'EO 2 way ducting', unit: 'm' },
      { id: 'vm-eo-3way', description: 'EO 3 way ducting', unit: 'm' },
      { id: 'vm-eo-4way', description: 'EO 4 way ducting', unit: 'm' },
      { id: 'vm-fw4', description: 'Virgin Footway Box FW4', unit: 'Nr' },
      { id: 'vm-fw6', description: 'Virgin Footway Box FW6', unit: 'Nr' },
      { id: 'vm-fw10', description: 'Virgin Footway Box FW10', unit: 'Nr' },
    ],
  },
  {
    id: 'streetlighting',
    label: 'Streetlighting',
    items: [
      { id: 'sl-main', description: 'Streetlighting main', unit: 'm' },
      { id: 'sl-connection-pits', description: 'Streetlight connection pits', unit: 'Nr' },
      { id: 'sl-columns', description: 'Streetlights & columns', unit: 'Nr' },
    ],
  },
  {
    id: 'combined-service',
    label: 'Combined Service',
    items: [
      { id: 'combined-service-trenching', description: 'Combined Service trenching', unit: 'm' },
    ],
  },
  {
    id: 'road-crossings',
    label: 'Road Crossings',
    items: [
      { id: 'rc-100', description: 'Road crossing duct n ex 100mmØ', unit: 'm' },
      { id: 'rc-150', description: '150mmØ duct', unit: 'm' },
      { id: 'rc-225', description: '225mmØ duct', unit: 'm' },
      { id: 'rc-300', description: '300mmØ duct', unit: 'm' },
      { id: 'rc-450', description: '450mmØ duct', unit: 'm' },
      { id: 'rc-600', description: '600mmØ duct', unit: 'm' },
    ],
  },
  {
    id: 'red-electric',
    label: 'Red Electric Ducts',
    items: [
      { id: 're-100', description: '100mmØ duct - Red electric', unit: 'm' },
      { id: 're-150', description: '150mmØ duct - Red electric', unit: 'm' },
    ],
  },
  {
    id: 'eo-live-footway',
    label: 'EO Live Footway',
    items: [
      { id: 'eo-live-footway', description: "EO for excavating service trenches off site in 'live' footway", unit: 'm' },
    ],
  },
];

/** Sums Main Services Bill Summary's Amount (Σ qty across roads × item-library rate
 * resolved via each item's evaluateCode) — shared by `MainServicesBillSummary.tsx` and
 * `useRoadsAndSewersTotalSync.ts` so the two can never drift. Rows with no code or no
 * library match contribute 0, same convention every other bill summary uses. */
export const sumMainServicesAmount = (
  quantities: Record<string, Record<string, number>>,
  getRate: (code: string) => number | undefined,
): number => {
  let total = 0;
  MAIN_SERVICE_TRENCHING_MAPPING.forEach((group) => {
    group.items.forEach((item) => {
      const roadQtys = quantities[item.id] || {};
      const totalQty = Object.values(roadQtys).reduce((s, q) => s + (q || 0), 0);
      if (totalQty <= 0) return;
      const code = findMainServiceTrenchingEvaluateCode(item.description);
      if (!code) return;
      const rate = getRate(code);
      if (rate == null) return;
      total += totalQty * rate;
    });
  });
  return total;
};
